import { Box, Button, MenuItem, Skeleton } from "@mui/material";
import { Select, TextField } from "formik-mui";
import { Field, Form, Formik } from "formik";
import { useSnackbar } from "notistack";

import { axios } from "../api";
import Sheet, {
    SheetContent,
    SheetHeader,
    SheetHeaderItem,
} from "../components/Sheet";
import LoadingSpinner from "../components/Loading";
import { CancelButton } from "../components/Buttons";

import { useConfig } from "../hooks/useConfig";
import { useGlobalConfig } from "../hooks/useGlobalConfig";

import { InvoiceLines } from "./types";
import { AxiosResponse } from "axios";

/** Match editable outlined fields — MUI disabled inputs otherwise use a darker border. */
const outlinedFieldBorderSx = {
    "& .MuiOutlinedInput-root fieldset": {
        borderColor: "rgba(0, 0, 0, 0.12)",
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.12)",
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
        borderColor: "rgba(0, 0, 0, 0.12)",
    },
    "& .MuiOutlinedInput-root.Mui-disabled fieldset": {
        borderColor: "rgba(0, 0, 0, 0.12)",
    },
};

function roundingFromConfig(
    globalConfig: Record<string, unknown> | undefined,
    primaryKey: string,
    fallbackKey: string,
    defaultVal: number
): number {
    const raw = globalConfig?.[primaryKey] ?? globalConfig?.[fallbackKey];
    const n = Number(raw);
    return Number.isFinite(n) ? n : defaultVal;
}

function formatRounded(value: unknown, decimals: number): string {
    if (value == null || value === "") return "";
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(decimals) : "";
}

export interface InvoiceEditPartProps {
    open: boolean;
    onClose: () => void;
    invoicePart: InvoiceLines;
}



const InvoiceEditPart: React.FC<InvoiceEditPartProps> = (props) => {
    const {
        open,
        onClose,
        invoicePart,
    } = props;
    const invoiceConfig = useConfig("invoices");

    return (
        <Sheet open={open} onClose={onClose}>
            <SheetHeader onClose={onClose} title="Edit Part">
                <SheetHeaderItem label="P/N" content={invoicePart?.part_number || ""}></SheetHeaderItem>
                <SheetHeaderItem
                    label="Description"
                    content={invoicePart?.line_description || ""}
                ></SheetHeaderItem>
            </SheetHeader>
            <SheetContent>
              {
                invoicePart && invoiceConfig ?(
                    <InvoiceEditPartForm invoiceConfig={invoiceConfig} invoicePart={invoicePart} onClose={onClose} />
                ):(
                   <LoadingSpinner/>
                )
              }
            </SheetContent>
        </Sheet>
    );
};

interface InvoiceEditPartFormProps {
    invoicePart: InvoiceLines;
    invoiceConfig: any;
    onClose: () => void;
}

const InvoiceEditPartForm: React.FC<InvoiceEditPartFormProps> = (props) => {
    const { invoicePart, invoiceConfig, onClose } = props
    const { enqueueSnackbar } = useSnackbar()
    const globalConfig = useGlobalConfig();

    const quantityRounding = roundingFromConfig(
        globalConfig,
        "global_quantity_rounding",
        "quantity_rounding",
        2
    );
    const costRounding = roundingFromConfig(
        globalConfig,
        "global_cost_rounding",
        "cost_rounding",
        4
    );
    const valueRounding = roundingFromConfig(
        globalConfig,
        "global_value_rounding",
        "value_rounding",
        4
    );

    if (!globalConfig) {
        return <LoadingSpinner />;
    }

    const initialValues = {
        quantity: formatRounded(invoicePart.quantity, quantityRounding),
        sell: formatRounded(invoicePart.sell, valueRounding),
        buy: formatRounded(invoicePart.buy, valueRounding),
        price: formatRounded(invoicePart.price, costRounding),
        uom: invoicePart.uom,
        department: invoicePart.type_id,
        discount: formatRounded(invoicePart.discount, quantityRounding),
        serial_number: invoicePart.serail_number,
        batch: invoicePart.batch,
        note: invoicePart.note
    }

    return (
        <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting })=>{
            const invoiceLine  = Object.entries(values).reduce((acc,[key,value])=>{
                acc[key] = value

                return acc
            },{} as InvoiceLines)
            axios({
                method: "put",
                url: `/v6/invoice/line/${invoicePart.id}`,
                data: invoiceLine,
            }).then((resp:AxiosResponse<InvoiceLines>)=>{
                setSubmitting(false);
                enqueueSnackbar("Successfully updated",{
                    variant:"success"
                })
                onClose()
            }).catch((error)=>{
                console.log(error,"error")
                setSubmitting(false);
                enqueueSnackbar("Error updating invoice item",{
                    variant:"error"
                })
            })
        }}
        >
            {({ values, setFieldValue, handleBlur, isSubmitting, submitForm }) => (
            <Form>
                <div style={{ display: "flex", width: "100%" }}>
                    <Field
                        fullWidth
                        component={TextField}
                        name="quantity"
                        label="Quantity"
                        sx={{ ...outlinedFieldBorderSx, flex: 1, marginRight: "1rem" }}
                        InputProps={{
                            disabled: true,
                        }}
                    />
                    <div style={{ flex: 1 }}>
                        <Field
                            component={TextField}
                            name="uom"
                            id="uom"
                            label="Unit"
                            sx={{
                                ...outlinedFieldBorderSx,
                                width: "100%",
                            }}
                            formControl={{
                                fullWidth: true,
                            }}
                            InputProps={{
                                disabled: true,
                            }}
                        />
                    </div>
                </div>
                <Field
                    fullWidth
                    name="price"
                    component={TextField}
                    label="Cost (Per Unit)"
                    sx={outlinedFieldBorderSx}
                    InputProps={{
                        disabled: true,
                    }}
                />
                <Field
                    fullWidth
                    name="buy"
                    label="Unit Price (Per Unit)"
                    component={TextField}
                    inputProps={{ inputMode: "decimal" }}
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                        setFieldValue(
                            "buy",
                            formatRounded(values.buy, valueRounding)
                        );
                        handleBlur(event);
                    }}
                />
                <Field
                    fullWidth
                    name="discount"
                    label="Discount %"
                    component={TextField}
                    inputProps={{ inputMode: "decimal" }}
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                        setFieldValue(
                            "discount",
                            formatRounded(values.discount, quantityRounding)
                        );
                        handleBlur(event);
                    }}
                />
                <Field
                    fullWidth
                    name="sell"
                    label="Sell Price (Per Unit)"
                    component={TextField}
                    inputProps={{ inputMode: "decimal" }}
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                        setFieldValue(
                            "sell",
                            formatRounded(values.sell, valueRounding)
                        );
                        handleBlur(event);
                    }}
                />
                <Field
                    fullWidth
                    name="serial_number"
                    label="Serial Number"
                    component={TextField}
                    sx={outlinedFieldBorderSx}
                    InputProps={{
                        disabled: true,
                    }}
                />
                <Field
                    fullWidth
                    name="batch"
                    label="Batch Number"
                    component={TextField}
                    sx={outlinedFieldBorderSx}
                    InputProps={{
                        disabled: true,
                    }}
                />
                {
                    invoiceConfig ? (
                        <Field
                            required
                            component={Select}
                            id="department"
                            name="department"
                            label="Department *"
                        >
                            {Object.entries(invoiceConfig.line_item_types).map(
                                ([key, value]) => (
                                    <MenuItem value={key} key={key}>
                                        {value as unknown as string}
                                    </MenuItem>
                                )
                            )}
                        </Field>
                    ) : (
                        <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
                    )
                }
                <Field
                    component={TextField}
                    fullWidth
                    multiline
                    rows="4"
                    name="note"
                    label="Notes"
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <CancelButton onClick={onClose} />
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={submitForm}
                    >
                        Submit
                    </Button>
                </Box>
                {isSubmitting && <LoadingSpinner />}
            </Form>
            )}
        </Formik>
    )
}


export default InvoiceEditPart;
